import React, { useState, useEffect, useRef, useCallback } from 'react';
import { forgeSentencesApi } from '../utils/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Search,
  Volume2,
  Loader2,
  Languages,
  BookOpen,
  Play,
  Pause,
  X,
  Pencil,
  Save,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

// Language names mapping
const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
  pt: 'Portuguese', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ar: 'Arabic', hi: 'Hindi', tr: 'Turkish', nl: 'Dutch', pl: 'Polish',
  sv: 'Swedish', da: 'Danish', no: 'Norwegian', fi: 'Finnish', cs: 'Czech',
  el: 'Greek', he: 'Hebrew', hu: 'Hungarian', id: 'Indonesian', ms: 'Malay',
  th: 'Thai', vi: 'Vietnamese', uk: 'Ukrainian', ro: 'Romanian', bg: 'Bulgarian',
  hr: 'Croatian', sk: 'Slovak', sl: 'Slovenian', sr: 'Serbian', ca: 'Catalan',
  et: 'Estonian', lv: 'Latvian', lt: 'Lithuanian', mk: 'Macedonian', sq: 'Albanian',
  bs: 'Bosnian', is: 'Icelandic', gl: 'Galician', eu: 'Basque', af: 'Afrikaans',
  fa: 'Persian', bn: 'Bengali', ta: 'Tamil', te: 'Telugu', ml: 'Malayalam',
  ka: 'Georgian', hy: 'Armenian', ur: 'Urdu', si: 'Sinhala', tl: 'Filipino',
  sw: 'Swahili', zu: 'Zulu', ig: 'Igbo', yo: 'Yoruba', ha: 'Hausa',
  am: 'Amharic', ne: 'Nepali', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada',
  pa: 'Punjabi', my: 'Burmese', km: 'Khmer', lo: 'Lao', mn: 'Mongolian',
  az: 'Azerbaijani', uz: 'Uzbek', kk: 'Kazakh', ky: 'Kyrgyz', tg: 'Tajik',
  tk: 'Turkmen', ps: 'Pashto', sd: 'Sindhi', ku: 'Kurdish', yi: 'Yiddish',
  xh: 'Xhosa',
};

// The 20 Spearhead Languages for FORGE Path (shown first)
const SPEARHEAD_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'ru', 'zh', 'fa', 'pt', 'ko', 'it',
  'ja', 'sw', 'ps', 'ar', 'hi', 'tr', 'id', 'th', 'vi', 'xh'
];

export default function SentenceExplorer() {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Selected sentence state
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [loadingSentence, setLoadingSentence] = useState(false);
  
  // Audio state
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);

  // Edit state
  const [editTarget, setEditTarget] = useState(null); // { mode: 'source'|'translation', langCode, text }
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Refs
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearching(true);
    try {
      const res = await forgeSentencesApi.search(query);
      setSearchResults(res.data.sentences || []);
      setShowDropdown(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Select a sentence
  const handleSelectSentence = async (sentence) => {
    setShowDropdown(false);
    setSearchQuery(sentence.source_text);
    setLoadingSentence(true);
    
    try {
      const res = await forgeSentencesApi.get(sentence.sentence_id);
      setSelectedSentence(res.data);
    } catch (err) {
      toast.error('Failed to load sentence details');
    } finally {
      setLoadingSentence(false);
    }
  };

  // Clear selection
  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSentence(null);
    setShowDropdown(false);
    stopAudio();
    searchInputRef.current?.focus();
  };

  // Play audio
  const playAudio = (sentenceId, languageCode) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audioUrl = forgeSentencesApi.getAudio(sentenceId, languageCode);
    audioRef.current = new Audio(audioUrl);
    audioRef.current.play();
    setPlayingAudio(languageCode);
    
    audioRef.current.onended = () => setPlayingAudio(null);
    audioRef.current.onerror = () => {
      toast.error('Failed to play audio');
      setPlayingAudio(null);
    };
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(null);
  };

  // Open edit dialog
  const openEdit = (mode, langCode, currentText) => {
    setEditTarget({ mode, langCode });
    setEditText(currentText || '');
  };

  // Save edit
  const saveEdit = async () => {
    if (!editTarget || !selectedSentence) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error('Text cannot be empty');
      return;
    }
    setSaving(true);
    try {
      if (editTarget.mode === 'source') {
        await forgeSentencesApi.update(selectedSentence.sentence_id, { source_text: trimmed });
      } else {
        await forgeSentencesApi.updateTranslation(
          selectedSentence.sentence_id,
          editTarget.langCode,
          { text: trimmed }
        );
      }
      // Refresh sentence details
      const res = await forgeSentencesApi.get(selectedSentence.sentence_id);
      setSelectedSentence(res.data);
      toast.success('Sentence updated');
      setEditTarget(null);
      setEditText('');
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to update sentence');
    } finally {
      setSaving(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        !searchInputRef.current?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      stopAudio();
    };
  }, []);

  // Get sorted translations (spearhead languages first, then alphabetically)
  const getSortedTranslations = () => {
    if (!selectedSentence?.translations) return [];
    
    const entries = Object.entries(selectedSentence.translations);
    const sourceLanguage = selectedSentence.source_language;
    
    return entries.sort((a, b) => {
      // Source language always first
      if (a[0] === sourceLanguage) return -1;
      if (b[0] === sourceLanguage) return 1;
      
      // Then spearhead languages
      const aIsSpearhead = SPEARHEAD_LANGUAGES.includes(a[0]);
      const bIsSpearhead = SPEARHEAD_LANGUAGES.includes(b[0]);
      
      if (aIsSpearhead && !bIsSpearhead) return -1;
      if (!aIsSpearhead && bIsSpearhead) return 1;
      
      // Within same group, sort by spearhead order or alphabetically
      if (aIsSpearhead && bIsSpearhead) {
        return SPEARHEAD_LANGUAGES.indexOf(a[0]) - SPEARHEAD_LANGUAGES.indexOf(b[0]);
      }
      
      // Non-spearhead: alphabetically by language name
      const nameA = LANGUAGE_NAMES[a[0]] || a[0];
      const nameB = LANGUAGE_NAMES[b[0]] || b[0];
      return nameA.localeCompare(nameB);
    });
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            <Languages className="inline-block w-8 h-8 mr-2 text-primary" />
            Sentence Explorer
          </h1>
          <p className="text-muted-foreground">
            Search and explore sentences in all available languages
          </p>
        </div>

        {/* Search Box */}
        <Card className="relative">
          <CardContent className="pt-6">
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Start typing a sentence in any language..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                    className="pl-10 pr-10 h-12 text-lg"
                    data-testid="sentence-search-input"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
                  )}
                </div>
                {(searchQuery || selectedSentence) && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleClear}
                    className="h-12 w-12"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                )}
              </div>

              {/* Autocomplete Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-2 bg-popover border rounded-lg shadow-lg max-h-80 overflow-y-auto"
                  data-testid="search-dropdown"
                >
                  {searchResults.map((sentence) => (
                    <div
                      key={sentence.sentence_id}
                      className="px-4 py-3 hover:bg-accent cursor-pointer border-b last:border-b-0 transition-colors"
                      onClick={() => handleSelectSentence(sentence)}
                      data-testid={`search-result-${sentence.sentence_id}`}
                    >
                      {/* Show the sentence in the matched language */}
                      <div className="font-medium">
                        {sentence.display_text || sentence.source_text}
                      </div>
                      {/* Show English translation if the matched language isn't English */}
                      {sentence.english_text && sentence.display_language !== 'en' && (
                        <div className="text-sm text-muted-foreground mt-1 italic">
                          {sentence.english_text}
                        </div>
                      )}
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {LANGUAGE_NAMES[sentence.display_language] || sentence.display_language || LANGUAGE_NAMES[sentence.source_language] || sentence.source_language}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          Course {sentence.course_id} / Lesson {sentence.lesson_id}
                        </Badge>
                        <Badge 
                          className={`text-xs ${
                            sentence.level === 'gold' ? 'bg-yellow-500 text-yellow-900' :
                            sentence.level === 'silver' ? 'bg-gray-300 text-gray-800' :
                            sentence.level === 'bronze' ? 'bg-amber-700' :
                            'bg-gray-500'
                          }`}
                        >
                          {sentence.level}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-2 bg-popover border rounded-lg shadow-lg p-4 text-center text-muted-foreground"
                >
                  No sentences found matching "{searchQuery}"
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loadingSentence && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Selected Sentence Translations */}
        {selectedSentence && !loadingSentence && (
          <div className="space-y-4">
            {/* Source Sentence Card */}
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Source Sentence
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {LANGUAGE_NAMES[selectedSentence.source_language] || selectedSentence.source_language}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit('source', selectedSentence.source_language, selectedSentence.source_text)}
                      className="gap-1 h-8"
                      data-testid="edit-source-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-medium">{selectedSentence.source_text}</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="secondary">
                    Course {selectedSentence.course_id} / Lesson {selectedSentence.lesson_id}
                  </Badge>
                  <Badge 
                    className={`${
                      selectedSentence.level === 'gold' ? 'bg-yellow-500 text-yellow-900' :
                      selectedSentence.level === 'silver' ? 'bg-gray-300 text-gray-800' :
                      selectedSentence.level === 'bronze' ? 'bg-amber-700' :
                      'bg-gray-500'
                    }`}
                  >
                    {selectedSentence.level}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Translations Count */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5" />
                Translations ({selectedSentence.languages_count || Object.keys(selectedSentence.translations || {}).length})
              </h2>
              <Badge variant="outline" className="bg-purple-100 dark:bg-purple-900">
                ⚔️ {SPEARHEAD_LANGUAGES.filter(l => selectedSentence.translations?.[l]?.text).length}/20 Spearhead
              </Badge>
            </div>

            {/* Translation Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getSortedTranslations().map(([langCode, data]) => (
                <Card 
                  key={langCode} 
                  className={`transition-all hover:shadow-md ${
                    langCode === selectedSentence.source_language 
                      ? 'border-primary/50 bg-primary/5' 
                      : SPEARHEAD_LANGUAGES.includes(langCode)
                        ? 'border-purple-200 dark:border-purple-800'
                        : ''
                  }`}
                  data-testid={`translation-card-${langCode}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                          {LANGUAGE_NAMES[langCode] || langCode}
                        </CardTitle>
                        {SPEARHEAD_LANGUAGES.includes(langCode) && langCode !== selectedSentence.source_language && (
                          <Badge variant="outline" className="text-xs bg-purple-100 dark:bg-purple-900 border-purple-300">
                            ⚔️
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">{langCode}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {data.text ? (
                      <>
                        <p className="text-base mb-3">{data.text}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {data.has_audio && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (playingAudio === langCode) {
                                  stopAudio();
                                } else {
                                  playAudio(selectedSentence.sentence_id, langCode);
                                }
                              }}
                              className="gap-2"
                              data-testid={`play-audio-${langCode}`}
                            >
                              {playingAudio === langCode ? (
                                <>
                                  <Pause className="w-4 h-4" />
                                  Stop
                                </>
                              ) : (
                                <>
                                  <Volume2 className="w-4 h-4" />
                                  Listen
                                </>
                              )}
                            </Button>
                          )}
                          {!data.has_audio && (
                            <span className="text-xs text-muted-foreground italic">
                              Audio not available
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit('translation', langCode, data.text)}
                            className="gap-1 ml-auto"
                            data-testid={`edit-translation-${langCode}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted-foreground italic">Translation not available</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedSentence && !loadingSentence && (
          <div className="text-center py-16 text-muted-foreground">
            <Languages className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Start typing to search for sentences</p>
            <p className="text-sm mt-2">
              Explore translations across {Object.keys(LANGUAGE_NAMES).length}+ languages
            </p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-sentence-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              Edit {editTarget?.mode === 'source' ? 'Source' : (LANGUAGE_NAMES[editTarget?.langCode] || editTarget?.langCode)} Sentence
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={3}
              className="text-base"
              placeholder="Enter the sentence..."
              data-testid="edit-sentence-textarea"
            />
            {editTarget?.mode === 'source' && (
              <p className="text-xs text-muted-foreground mt-2 italic">
                Note: Editing the source sentence will trigger retranslation into all languages.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={saving || !editText.trim()} className="gap-1" data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
